import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface CardScrollBodyProps {
  children: React.ReactNode;
  label: string;
  className?: string;
}

/** Not shadcn ScrollArea: its `display:table` viewport grows to content width and
 *  defeats the row truncation (measured 593px inside a 200px card). */
const CardScrollBody: React.FC<CardScrollBodyProps> = ({ children, label, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    // Rows arrive after the fetch, so a mount-time measurement would miss them.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, children]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={ref}
        onScroll={measure}
        className={cn('h-full overflow-y-auto overscroll-contain', className)}
        // A list that fits is not a landmark, and naming it would only add noise.
        {...(overflowing ? { role: 'region', 'aria-label': label, tabIndex: 0 } : {})}
      >
        {children}
      </div>

      {overflowing && !atEnd && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card to-transparent"
        />
      )}
    </div>
  );
};

export default CardScrollBody;
